import { supabase } from "@/integrations/supabase/client";

export type UazapiContactsSyncParams = {
  workspaceId: string;
  integrationId: string;
  funnelId: string;
  stageSlug: string;
  tagIds?: string[];
  contactScope?: "address_book" | "outside_address_book" | "all";
};

export type UazapiContactsSyncJobStatus = {
  id: string;
  status: "pending" | "processing" | "completed" | "failed";
  percent: number | null;
  total_records: number | null;
  fetched_total: number;
  created_total: number;
  skipped_existing_total: number;
  skipped_invalid_total: number;
  last_error: string | null;
};

type StartResponse = {
  success: boolean;
  error?: string;
  job?: UazapiContactsSyncJobStatus;
  already_running?: boolean;
};

type StatusResponse = {
  success: boolean;
  error?: string;
  job?: UazapiContactsSyncJobStatus;
};

export async function enqueueUazapiContactsSync(
  params: UazapiContactsSyncParams,
): Promise<{ jobId: string; alreadyRunning: boolean }> {
  const { data, error } = await supabase.functions.invoke("sync-uazapi-contacts", {
    body: {
      action: "start",
      workspace_id: params.workspaceId,
      integration_id: params.integrationId,
      funnel_id: params.funnelId,
      stage_slug: params.stageSlug,
      tag_ids: params.tagIds ?? [],
      contact_scope: params.contactScope ?? "address_book",
    },
  });

  if (error) {
    throw new Error(error.message || "Falha ao iniciar sincronização");
  }

  const payload = data as StartResponse | null;
  if (!payload?.success || !payload.job?.id) {
    throw new Error(payload?.error || "Falha ao iniciar sincronização");
  }

  return {
    jobId: payload.job.id,
    alreadyRunning: payload.already_running === true,
  };
}

export async function fetchUazapiContactsSyncJob(
  jobId: string,
): Promise<UazapiContactsSyncJobStatus> {
  const { data, error } = await supabase.functions.invoke("sync-uazapi-contacts", {
    body: { action: "status", job_id: jobId },
  });

  if (error) {
    throw new Error(error.message || "Falha ao consultar sincronização");
  }

  const payload = data as StatusResponse | null;
  if (!payload?.success || !payload.job) {
    throw new Error(payload?.error || "Falha ao consultar sincronização");
  }

  return payload.job;
}

const TERMINAL: UazapiContactsSyncJobStatus["status"][] = ["completed", "failed"];

export function isUazapiContactsSyncTerminal(status: string): boolean {
  return TERMINAL.includes(status as UazapiContactsSyncJobStatus["status"]);
}

/** Poll opcional (ex.: enquanto o modal está aberto). O job continua no servidor se o usuário sair. */
export async function pollUazapiContactsSyncJob(
  jobId: string,
  onUpdate: (job: UazapiContactsSyncJobStatus) => void,
  intervalMs = 2500,
): Promise<UazapiContactsSyncJobStatus> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const job = await fetchUazapiContactsSyncJob(jobId);
    onUpdate(job);
    if (isUazapiContactsSyncTerminal(job.status)) return job;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
